import React, { useState } from "react";
import Papa from "papaparse";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../design/table";
import {
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from "../../../design/dialog";
import { Button } from "../../../design/button";
import { Input } from "../../../design/input";
import { useEvent } from "../../../contexts/EventContext";
import { Select } from "../../../design/select";
import {
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm,
} from "../../../design/description-list";
import ProgressBar from "../../ProgressBar";

interface Props {
  handleClose: () => void;
}

const ImportCSV: React.FC<Props> = ({ handleClose }) => {
  const { createEvent } = useEvent();
  const [isSaving, setIsSaving] = useState(false);
  const [tableData, setTableData] = useState<string[][]>([]);
  const [eventDateIndex, setEventDateIndex] = useState<number | null>(null);
  const [descriptionIndex, setDescriptionIndex] = useState<number | null>(null);
  const [failedEvents, setFailedEvents] = useState<
    { eventDate: string; description: string }[]
  >([]);
  const [state, setState] = useState<"select_csv" | "import" | "failed_events">(
    "select_csv",
  );
  const [progress, setProgress] = useState(0);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    Papa.parse(file, {
      complete: (result) => {
        setTableData(result.data as string[][]);
      },
      skipEmptyLines: true,
    });

    setState("import");
  };

  const validateEvent = (event: { eventDate: string; description: string }) => {
    if (!event.eventDate || isNaN(new Date(event.eventDate).getTime()))
      return false;
    if (!event.description.trim()) return false;
    return true;
  };

  const handleSubmit = async () => {
    if (eventDateIndex === null || descriptionIndex === null) {
      alert("Please select both Event Date and Description columns.");
      return;
    }

    setIsSaving(true);

    const failedList: {
      eventDate: string;
      description: string;
      significance?: string;
    }[] = [];

    const events = tableData.slice(1).map((row) => {
      return {
        eventDate: row[eventDateIndex] || "",
        description: row[descriptionIndex] || "",
      };
    });

    for (let i = 0; i < events.length; i++) {
      const event = events[i];
      setProgress(Math.round(((i + 1) / events.length) * 100));

      if (validateEvent(event)) {
        await createEvent({
          eventDate: new Date(event.eventDate),
          description: event.description,
          participants: [],
          content: "",
        });
      } else {
        failedList.push(event);
      }
    }

    setIsSaving(false);

    if (failedList.length > 0) {
      setState("failed_events");
      setFailedEvents(failedList);
    } else {
      handleClose();
    }
  };

  const retryFailedImports = async () => {
    const newFailedList = [];

    for (const event of failedEvents) {
      if (validateEvent(event)) {
        await createEvent({
          eventDate: new Date(event.eventDate),
          description: event.description,
          participants: [],
          content: "",
        });
      } else {
        newFailedList.push(event);
      }
    }

    setFailedEvents(newFailedList);
    if (newFailedList.length === 0) {
      handleClose();
    }
  };

  const handleFailedEdit = (index: number, key: string, value: string) => {
    setFailedEvents((prev) =>
      prev.map((event, i) =>
        i === index ? { ...event, [key]: value } : event,
      ),
    );
  };

  return (
    <>
      {state === "select_csv" && (
        <>
          <DialogTitle>Upload CSV</DialogTitle>
          <DialogDescription>
            Upload a CSV file containing event data. Ensure the first row
            contains headers.
          </DialogDescription>
          <DialogBody>
            <Input type="file" accept=".csv" onChange={handleFileUpload} />
          </DialogBody>
          <DialogActions>
            <Button type="button" onClick={handleClose}>
              Cancel
            </Button>
          </DialogActions>
        </>
      )}
      {state === "import" && (
        <>
          <DialogTitle>Import Events</DialogTitle>
          <DialogDescription>
            Select the columns for Event Date, Description, and Significance
            (*optional).
          </DialogDescription>
          <DialogBody>
            {isSaving && <ProgressBar progress={progress} />}
            {!isSaving && (
              <Table>
                <TableHead>
                  <TableRow>
                    {tableData &&
                      tableData[0] &&
                      tableData[0].map((header, index) => (
                        <TableHeader key={index}>
                          <div className="flex flex-col items-center min-w-40">
                            <span>{header}</span>
                            <Select
                              value={
                                eventDateIndex === index
                                  ? "eventDate"
                                  : descriptionIndex === index
                                    ? "description"
                                    : ""
                              }
                              onChange={(e) => {
                                if (e.target.value === "eventDate") {
                                  setEventDateIndex(index);
                                } else if (e.target.value === "description") {
                                  setDescriptionIndex(index);
                                }
                              }}
                            >
                              <option value="">Select</option>
                              <option value="eventDate">Event Date</option>
                              <option value="description">Description</option>
                            </Select>
                          </div>
                        </TableHeader>
                      ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableData.slice(1, 4).map((row, rowIndex) => (
                    <TableRow key={rowIndex}>
                      {row.map((cell, cellIndex) => (
                        <TableCell
                          key={cellIndex}
                          className="p-2 border"
                          style={{ whiteSpace: "pre-wrap" }}
                        >
                          {cell}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </DialogBody>
          <DialogActions>
            {tableData.length !== 0 && (
              <Button
                onClick={() => {
                  setTableData([]);
                  setState("select_csv");
                }}
              >
                Back
              </Button>
            )}
            {tableData.length === 0 && (
              <Button onClick={handleClose}>Cancel</Button>
            )}
            {tableData.length !== 0 && (
              <Button type="button" onClick={handleSubmit} disabled={isSaving}>
                {isSaving ? "Importing..." : "Import"}
              </Button>
            )}
          </DialogActions>
        </>
      )}

      {state === "failed_events" && (
        <>
          <DialogTitle>Failed Imports</DialogTitle>
          <DialogDescription>
            Some events could not be imported. Please correct them and retry.
            Ensure that your data follows the correct format:
            <DescriptionList>
              <DescriptionTerm>Event Date</DescriptionTerm>
              <DescriptionDetails>
                MM/DD/YYYY HH:mm:ss (e.g., 7/1/2024 08:00:00)
              </DescriptionDetails>
              <DescriptionTerm>Description</DescriptionTerm>
              <DescriptionDetails>A brief event description</DescriptionDetails>
              <DescriptionTerm>Significance</DescriptionTerm>
              <DescriptionDetails>
                The importance or context of the event
              </DescriptionDetails>
            </DescriptionList>
          </DialogDescription>

          <DialogBody>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader>Event Date</TableHeader>
                  <TableHeader>Description</TableHeader>
                  <TableHeader>Significance</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {failedEvents.map((event, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Input
                        type="text"
                        placeholder="MM/DD/YYYY HH:mm:ss"
                        value={event.eventDate}
                        onChange={(e) =>
                          handleFailedEdit(index, "eventDate", e.target.value)
                        }
                        aria-label={`Edit Event Date for row ${index + 1}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="text"
                        placeholder="Enter description"
                        value={event.description}
                        onChange={(e) =>
                          handleFailedEdit(index, "description", e.target.value)
                        }
                        aria-label={`Edit Description for row ${index + 1}`}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </DialogBody>

          <DialogActions>
            <Button type="button" onClick={handleClose} plain>
              Cancel
            </Button>
            <Button type="button" onClick={retryFailedImports}>
              Retry Import
            </Button>
          </DialogActions>
        </>
      )}
    </>
  );
};

export default ImportCSV;
